import { storage as appStorage, type TypedStorage } from "./storage";

/**
 * When the book recommendation is allowed to appear, and for how long a
 * dismissal silences it. Pure rules over one persisted record, kept out of
 * the component so the decision is testable without rendering anything.
 *
 * The recommendation is a nudge, never a gate: nothing here can block a
 * round, take focus, or interrupt the loop. The strongest thing it does is
 * decide whether one small line of text is on screen.
 */

/** Where the persisted dismissal record lives. */
export const BOOK_NUDGE_KEY = "book:nudge";

/** How long a dismissal silences the recommendation. */
export const DISMISS_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The session-depth milestone the recommendation is tied to: the moment a
 * sitting reaches this many counted rounds. Deliberately the *exact* count
 * rather than "at least this many": the nudge marks one moment, so it shows
 * on the round that crosses the line and is gone again by the next one. That
 * is what keeps it from becoming a permanent fixture of the panel area
 * after a long sitting, without needing a timer or a second latch.
 *
 * "Counted rounds" is `SessionSummary.rounds` (lib/sessionSummary.ts), which
 * already excludes practice replays, so this reads the same number the
 * session summary prints rather than counting anything itself.
 */
export const MILESTONE_ROUNDS = 10;

/**
 * What is remembered about the recommendation between page loads. Both
 * fields are absent until something happens, so a fresh browser stores
 * nothing at all.
 */
export type BookNudgeState = {
  /** `Date.now()` of the last dismissal, if any. */
  dismissedAt?: number;
  /** True once an outbound link has been clicked. Permanent. */
  clicked?: boolean;
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

/** Read the record back, treating anything malformed as "nothing stored". */
export function readBookNudgeState(store: TypedStorage = appStorage): BookNudgeState {
  const raw = store.get<unknown>(BOOK_NUDGE_KEY, null);
  if (!isRecord(raw)) return {};
  const state: BookNudgeState = {};
  if (typeof raw.dismissedAt === "number" && Number.isFinite(raw.dismissedAt))
    state.dismissedAt = raw.dismissedAt;
  if (raw.clicked === true) state.clicked = true;
  return state;
}

/**
 * Whether the recommendation is silenced right now. A click silences it for
 * good (the reader has been sent to the book; asking again is nagging), a
 * dismissal for `DISMISS_DAYS`. A `dismissedAt` in the future (a clock that
 * moved backwards) is treated as a dismissal just now rather than as an
 * expired one.
 */
export function isBookNudgeSuppressed(state: BookNudgeState, now: number): boolean {
  if (state.clicked) return true;
  if (typeof state.dismissedAt !== "number") return false;
  return now - state.dismissedAt < DISMISS_DAYS * DAY_MS;
}

/** Whether the session-milestone placement should render. */
export function shouldShowMilestoneNudge(
  rounds: number,
  state: BookNudgeState,
  now: number,
): boolean {
  if (rounds !== MILESTONE_ROUNDS) return false;
  return !isBookNudgeSuppressed(state, now);
}

/** Record a dismissal, leaving a previous click flag in place. */
export function dismissBookNudge(
  now: number = Date.now(),
  store: TypedStorage = appStorage,
): BookNudgeState {
  const next: BookNudgeState = { ...readBookNudgeState(store), dismissedAt: now };
  store.set(BOOK_NUDGE_KEY, next);
  return next;
}

/** Record an outbound click, which silences the recommendation permanently. */
export function markBookNudgeClicked(store: TypedStorage = appStorage): BookNudgeState {
  const next: BookNudgeState = { ...readBookNudgeState(store), clicked: true };
  store.set(BOOK_NUDGE_KEY, next);
  return next;
}
